import type { AcpClientHandle, AcpClientOptions } from './clients/acp-client.js';
import {
  BACKEND_LOCAL_AGENT_TYPE as CLAUDE_CODE_AGENT_TYPE,
  buildClaudeCodeAcpSpawnCommand,
  createClaudeCodeAcpClient,
} from './clients/claude-code-acp-client.js';
import {
  BACKEND_LOCAL_AGENT_TYPE as CODEX_ACP_AGENT_TYPE,
  buildCodexAcpSpawnCommand,
  createCodexAcpClient,
  DEFAULT_CODEX_ACP_COMMAND,
  isCodexAcpCommand,
} from './clients/codex-acp-client.js';
import {
  BACKEND_LOCAL_AGENT_TYPE as CURSOR_CLI_AGENT_TYPE,
  buildCursorAcpSpawnCommand,
  createCursorAcpClient,
} from './clients/cursor/cursor-acp-client.js';
import {
  BACKEND_LOCAL_AGENT_TYPE as KIRO_ACP_AGENT_TYPE,
  buildKiroAcpSpawnCommand,
  createKiroAcpClient,
  DEFAULT_KIRO_ACP_COMMAND,
  isKiroAcpCommand,
} from './clients/kiro-acp-client.js';

/** Default CLI commands per backend agent type (keys use each client’s `BACKEND_LOCAL_AGENT_TYPE`). */
const AGENT_TYPE_DEFAULT_COMMANDS: Record<string, string[]> = {
  [CURSOR_CLI_AGENT_TYPE]: ['agent', 'acp'],
  [CODEX_ACP_AGENT_TYPE]: [...DEFAULT_CODEX_ACP_COMMAND],
  /** ACP stdio agent; `@anthropic-ai/claude-code` is the interactive CLI and does not speak ACP on stdout. */
  [CLAUDE_CODE_AGENT_TYPE]: ['npx', '--yes', '@agentclientprotocol/claude-agent-acp'],
  /** [Kiro CLI ACP](https://kiro.dev/docs/cli/acp/) — use full path to `kiro-cli` in PATH if the IDE cannot find it. */
  [KIRO_ACP_AGENT_TYPE]: [...DEFAULT_KIRO_ACP_COMMAND],
};

/** Human-readable names for known backend agent type codes (align with app bridge agent labels). */
const AGENT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  [CURSOR_CLI_AGENT_TYPE]: 'Cursor',
  [CODEX_ACP_AGENT_TYPE]: 'Codex',
  [CLAUDE_CODE_AGENT_TYPE]: 'Claude Code',
  [KIRO_ACP_AGENT_TYPE]: 'Kiro',
};

/**
 * Map a backend agent type string (e.g. cursor-cli) to a short label for logs.
 * Unknown codes become title-cased words from kebab/snake case.
 */
export function getAgentTypeDisplayName(agentType: string | null | undefined): string {
  if (agentType == null || agentType === '') return 'Unknown agent';
  const known = AGENT_TYPE_DISPLAY_NAMES[agentType];
  if (known) return known;
  return agentType
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function useCursorAcp(agentType: string | null, command: string[]): boolean {
  if (agentType === CURSOR_CLI_AGENT_TYPE) return true;
  return command[0] === 'agent' && command[1] === 'acp';
}

function useCodexAcp(agentType: string | null, command: string[]): boolean {
  if (agentType === CODEX_ACP_AGENT_TYPE) return true;
  return isCodexAcpCommand(command);
}

function useKiroAcp(agentType: string | null, command: string[]): boolean {
  if (agentType === KIRO_ACP_AGENT_TYPE) return true;
  return isKiroAcpCommand(command);
}

export interface ResolvedAgentCommand {
  command: string[];
  label: string;
  createClient: (options: AcpClientOptions) => Promise<AcpClientHandle>;
  /** Spawn argv for subprocess (e.g. Cursor `--mode`); `ensureAcpClient` also keys on `agentConfig` when set (Claude permission). */
  spawnCommandForSession: (sessionMode: string | undefined, agentConfig?: Record<string, unknown> | null) => string[];
}

/**
 * Determine which ACP command and client factory to use from the backend agent type.
 * Returns null when no command can be resolved.
 */
export function resolveAgentCommand(preferredAgentType: string | null): ResolvedAgentCommand | null {
  if (!preferredAgentType) return null;
  const command = AGENT_TYPE_DEFAULT_COMMANDS[preferredAgentType];
  if (!command?.length) return null;

  if (useCursorAcp(preferredAgentType, command)) {
    return {
      command,
      label: preferredAgentType,
      createClient: createCursorAcpClient,
      spawnCommandForSession: (sessionMode, _agentConfig) => buildCursorAcpSpawnCommand(command, sessionMode),
    };
  }
  if (useCodexAcp(preferredAgentType, command)) {
    return {
      command,
      label: preferredAgentType,
      createClient: createCodexAcpClient,
      spawnCommandForSession: (sessionMode, agentConfig) => buildCodexAcpSpawnCommand(command, sessionMode, agentConfig),
    };
  }
  if (useKiroAcp(preferredAgentType, command)) {
    return {
      command,
      label: preferredAgentType,
      createClient: createKiroAcpClient,
      spawnCommandForSession: (sessionMode, _agentConfig) => buildKiroAcpSpawnCommand(command, sessionMode),
    };
  }
  return {
    command,
    label: preferredAgentType,
    createClient: createClaudeCodeAcpClient,
    spawnCommandForSession: (sessionMode) => buildClaudeCodeAcpSpawnCommand(command, sessionMode),
  };
}
